import { Inject, Injectable } from '@angular/core';
import { TeethUnitType } from '@shared/models/rx-rules-json-interface';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { IdName } from '@shared/models/id-name';
import { unitTypeSortOrder, bridgeUnitType } from '@shared/models/consts';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TranslateService } from '@ngx-translate/core';
import { MaterialToothEditorRule } from '@shared/models/material-tooth-editor-rule';
import { UNIT_TYPE_MATERIAL_TOOTH_EDITOR_RULES } from '@shared/rules/rules-tokens';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { ProcedureRulesParser } from '@shared/services/rx-rules-helper/procedure-rules.parser';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { PROCEDURE_IN_BRIDGE_RULES_PARSER, PROCEDURE_RULES_PARSER } from '@shared/services/rx-rules-helper/parser-tokens';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { ProcedureMapCheckedValue } from '@core/procedure-helpers/models/procedure-map-composites';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { Maybe } from '@/types/globals';
import { ShellQuery } from '@shared/store/shell/shell-query';

@Injectable({ providedIn: 'root' })
export class RxIndicationRulesService {
	constructor(
		@Inject(UNIT_TYPE_MATERIAL_TOOTH_EDITOR_RULES) private readonly unitTypeMaterialToothEditorRules: MaterialToothEditorRule[],
		@Inject(PROCEDURE_RULES_PARSER) private readonly procedureRulesParser: ProcedureRulesParser<UnitTypes>,
		@Inject(PROCEDURE_IN_BRIDGE_RULES_PARSER) private readonly procedureInBridgeRulesParser: ProcedureRulesParser<UnitTypesInBridge>,
		private readonly translateService: TranslateService,
		private readonly shellQuery: ShellQuery
	) {}

	readonly innerBridgeTypes: UnitTypesInBridge[] = [
		UnitTypesInBridge.Abutment,
		UnitTypesInBridge.Pontic,
		UnitTypesInBridge.Inlay,
		UnitTypesInBridge.Onlay,
		UnitTypesInBridge.ImplantBased,
		UnitTypesInBridge.EggshellBridge,
		UnitTypesInBridge.MockupBridge,
		UnitTypesInBridge.Missing
	];
	readonly outerBridgeTypes: UnitTypesInBridge[] = [
		UnitTypesInBridge.Abutment,
		UnitTypesInBridge.Pontic,
		UnitTypesInBridge.Maryland,
		UnitTypesInBridge.Inlay,
		UnitTypesInBridge.Onlay,
		UnitTypesInBridge.ImplantBased,
		UnitTypesInBridge.EggshellBridge,
		UnitTypesInBridge.MockupBridge
	];

	getProcedureFlowUnitType(
		tooth: Tooth,
		procedureMap: Maybe<ProcedureMapCheckedValue>,
		rxConfiguration: Maybe<RxConfiguration>,
		withIteroModelling: boolean,
		withBridges: boolean
	) {
		if (tooth.ToothID == null) {
			return [];
		}

		let unitTypeIds = this.procedureRulesParser.getUnitTypeIdsByProcedureMapAndToothId(
			procedureMap,
			tooth.ToothID,
			tooth.UnitTypeID,
			rxConfiguration ?? undefined
		);

		if (!withIteroModelling) {
			unitTypeIds = this.removeModelingSpecificUnitTypes(unitTypeIds);
		}
		if (withBridges) {
			// @ts-expect-error - TODO: fix type of unitTypeIds to accept `null`
			unitTypeIds.push(bridgeUnitType);
		}

		return unitTypeIds;
	}

	getProcedureFlowUnitTypeNames(
		tooth: Tooth,
		procedureMap: Maybe<ProcedureMapCheckedValue>,
		rxConfiguration: Maybe<RxConfiguration>,
		withIteroModelling: boolean,
		withBridges: boolean,
		getUnitTypeTranslatedNameOverride?: (unitTypeId: number) => string
	): IdName[] {
		const unitTypeIds = this.getProcedureFlowUnitType(tooth, procedureMap, rxConfiguration, withIteroModelling, withBridges);

		const shouldSortByDefaultOrder = procedureMap?.ProcedureId !== (ProcedureEnum.Denture_Removable as number);

		return this.getFilteredAndCustomSortedUnitTypeMenuNames({
			unitTypeIds,
			shouldSortByDefaultOrder,
			getUnitTypeTranslatedNameOverride
		});
	}

	/**
	 * v1 backward-compat wrapper: reads `rxConfiguration` from {@link ShellQuery} so legacy callers
	 * (`tooth-editor.facade.ts`, `teeth-diagram.facade.ts`) don't need to thread it through.
	 *
	 * New code should call {@link getProcedureFlowUnitTypeIdsForBridge} directly with an explicit
	 * `rxConfiguration` argument.
	 */
	getProcedureFlowUnitTypeNamesForBridge(pmToCheck: Maybe<ProcedureMapCheckedValue>, tooth: Tooth, isOuterTooth: boolean): IdName[] {
		const allowedIds = this.getProcedureFlowUnitTypeIdsForBridge(pmToCheck, tooth, isOuterTooth, this.shellQuery.rxConfiguration);

		return allowedIds.map(id => ({ Id: id, Name: this.getBridgeUnitTypeTranslatedName(id) }));
	}

	/**
	 * Returns the bridge unit type ids allowed for a single tooth in a given procedure map,
	 * intersected with the static inner/outer position set.
	 *
	 * Used by the intuitive flow to compute per-tooth allowed indications and intersect across
	 * a multi-tooth selection. Pure: takes `rxConfiguration` as an explicit argument (no hidden
	 * dependency on {@link ShellQuery}) — symmetric with {@link getProcedureFlowUnitType}.
	 */
	getProcedureFlowUnitTypeIdsForBridge(
		pmToCheck: Maybe<ProcedureMapCheckedValue>,
		tooth: Tooth,
		isOuterTooth: boolean,
		rxConfiguration: Maybe<RxConfiguration>
	): UnitTypesInBridge[] {
		const allowedByPosition = isOuterTooth ? this.outerBridgeTypes : this.innerBridgeTypes;
		const unitTypeIds = this.procedureInBridgeRulesParser.getUnitTypeIdsByProcedureMapAndToothId(
			pmToCheck,
			tooth.ToothID ?? 0,
			tooth.ToothInBridgeTypeID ?? 0,
			rxConfiguration ?? undefined
		);

		return allowedByPosition.filter(id => unitTypeIds.includes(id));
	}

	/**
	 * Returns the union of bridge unit type ids allowed for a procedure (any position),
	 * intersected with the static inner ∪ outer position set.
	 *
	 * Used by the intuitive bridge flow to compute the *visible* set of indication buttons
	 * (independent of tooth selection).
	 */
	getProcedureUnitTypeIdsForBridge(
		procedureMap: Maybe<ProcedureMapCheckedValue>,
		rxConfiguration: Maybe<RxConfiguration>
	): UnitTypesInBridge[] {
		const allowedByPosition = new Set<UnitTypesInBridge>([...this.outerBridgeTypes, ...this.innerBridgeTypes]);
		const unitTypeIds = this.procedureInBridgeRulesParser.getUnitTypeIdsByProcedure(procedureMap, rxConfiguration);

		return unitTypeIds.filter((id): id is UnitTypesInBridge => allowedByPosition.has(id as UnitTypesInBridge));
	}

	sort(unitTypeIds: (UnitTypes | null)[]): (UnitTypes | null)[] {
		return unitTypeSortOrder.filter(sortedItem => unitTypeIds.includes(sortedItem));
	}

	getFilteredAndCustomSortedUnitTypeMenuNames({
		unitTypeIds,
		shouldSortByDefaultOrder = true,
		getUnitTypeTranslatedNameOverride
	}: {
		unitTypeIds: number[];
		shouldSortByDefaultOrder?: boolean;
		getUnitTypeTranslatedNameOverride?: (unitTypeId: number) => string;
	}): IdName[] {
		const getUnitTypeName = getUnitTypeTranslatedNameOverride ?? ((id: number) => this.getUnitTypeTranslatedName(id));
		const sortedOrder = shouldSortByDefaultOrder ? this.sort(unitTypeIds) : unitTypeIds;

		return sortedOrder
			.map(id => {
				return id
					? { Id: id, Name: getUnitTypeName(id) }
					: { Id: bridgeUnitType, Name: this.translateService.instant('UnitTypes.Bridge') };
			})
			.filter(Boolean);
	}

	getUnitTypeTranslatedName(unitTypeId: number): string {
		return this.translateService.instant(`UnitTypes.${UnitTypes[unitTypeId]}`);
	}

	getBridgeUnitTypeTranslatedName(bridgeUnitTypeId: number): string {
		return this.translateService.instant(`BridgeUnitTypes.${UnitTypesInBridge[bridgeUnitTypeId]}`);
	}

	removeModelingSpecificUnitTypes(modelingSpecificUnitTypeIds: number[]): number[] {
		return modelingSpecificUnitTypeIds.filter(
			modelingSpecificUnitTypeId =>
				modelingSpecificUnitTypeId !== (UnitTypes.Crown_ThreeQuarters as number) &&
				modelingSpecificUnitTypeId !== (UnitTypes.Detachable as number)
		);
	}

	getProcedureFlowUnitTypeConfiguration(rx: Maybe<RxModel>): TeethUnitType[] | null {
		return this.procedureRulesParser.getTeethUnitTypeRule({
			ProcedureId: rx?.Order?.ProcedureId,
			TypeId: rx?.Order?.ProcedureTypeId,
			Id: rx?.Order?.ProcedureMapId
		});
	}

	private procedureUnitTypesInBridgeByToothPosition(isOuterTooth: boolean): IdName[] {


		const bridgeTypesToShow = isOuterTooth ? this.outerBridgeTypes : this.innerBridgeTypes;

		return bridgeTypesToShow.map(id => {
			const name = this.getBridgeUnitTypeTranslatedName(id);

			return { Id: id, Name: name };
		});
	}
}
